import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useGetDailySlotsQuery, useCreateCustomBookingMutation } from "../store/api/servicesApi";

const STEPS = ["Date & time", "Service details", "Contact info", "Review"];

function StepIndicator({ current }) {
  return (
    <div className="flex flex-col items-center gap-2 mb-8">
      <div className="flex items-center justify-center">
        {STEPS.map((label, i) => {
          const done = i < current;
          const active = i === current;
          return (
            <div key={label} className="flex items-center">
              <div className="flex flex-col items-center gap-1.5">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-manrope-bold transition-all ${
                  done ? "bg-primary text-white" : active ? "bg-primary text-white ring-4 ring-primary/20" : "bg-gray-100 text-gray-400"
                }`}>
                  {done ? (
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2.5} stroke="currentColor" className="w-4 h-4">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                    </svg>
                  ) : i + 1}
                </div>
                <span className={`hidden sm:block text-[11px] font-manrope-medium whitespace-nowrap ${active ? "text-primary" : "text-gray-400"}`}>{label}</span>
              </div>
              {i < STEPS.length - 1 && (
                <div className={`w-8 sm:w-20 h-0.5 sm:mb-5 mx-1.5 transition-all ${done ? "bg-primary" : "bg-gray-100"}`} />
              )}
            </div>
          );
        })}
      </div>
      {/* Current step label — shown on mobile only, where per-step labels are hidden */}
      <span className="sm:hidden font-manrope-semibold text-sm text-primary">
        Step {current + 1} of {STEPS.length} · {STEPS[current]}
      </span>
    </div>
  );
}

function getTodayStr() {
  return new Date().toISOString().split("T")[0];
}

export default function CustomBooking() {
  const { user, openModal } = useAuth();
  const navigate = useNavigate();

  const [step, setStep] = useState(0);
  const [form, setForm] = useState({
    date: "",
    slotId: "",
    serviceDescription: "",
    name: user?.name || "",
    email: user?.email || "",
    phone: user?.phone || "",
    address: user?.address || "",
    notes: "",
  });
  const [error, setError] = useState("");
  const [submitted, setSubmitted] = useState(null);

  const { data: slotsData, isLoading: loadingSlots } = useGetDailySlotsQuery();
  const [createBooking, { isLoading: isSubmitting }] = useCreateCustomBookingMutation();

  const slots = slotsData?.data || [];
  const selectedSlot = slots.find((s) => s.id === form.slotId);

  const set = (key) => (e) => {
    setForm((f) => ({ ...f, [key]: e.target.value }));
    setError("");
  };

  const validate = () => {
    if (step === 0) {
      if (!form.date) return "Please select a date.";
      if (form.date < getTodayStr()) return "Date cannot be in the past.";
      if (!form.slotId) return "Please select a time slot.";
    }
    if (step === 1) {
      if (!form.serviceDescription.trim()) return "Please describe the service you need.";
      if (form.serviceDescription.trim().length < 20) return "Description must be at least 20 characters.";
    }
    if (step === 2) {
      if (!form.name.trim()) return "Name is required.";
      if (!form.email.trim()) return "Email is required.";
    }
    return "";
  };

  const next = () => {
    const err = validate();
    if (err) { setError(err); return; }
    setError("");
    setStep((s) => s + 1);
  };

  const back = () => { setError(""); setStep((s) => s - 1); };

  const handleSubmit = async () => {
    if (!user) { openModal("sign-in"); return; }
    setError("");
    try {
      const payload = {
        preferredDate: form.date,
        preferredTimeSlotId: form.slotId,
        serviceDescription: form.serviceDescription,
        customerName: form.name,
        customerEmail: form.email,
        customerPhone: form.phone,
        address: form.address,
        notes: form.notes,
      };
      const res = await createBooking(payload).unwrap();
      setSubmitted(res.data);
    } catch (err) {
      setError(err?.data?.error?.message || "Failed to submit request. Please try again.");
    }
  };

  if (submitted) {
    return (
      <div className="bg-bg min-h-screen py-16">
        <div className="container mx-auto px-4 max-w-lg text-center flex flex-col items-center gap-6">
          <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-10 h-10 text-green-500">
              <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
            </svg>
          </div>
          <div>
            <h2 className="font-manrope-bold text-2xl text-black mb-2">Request submitted!</h2>
            <p className="font-manrope-regular text-sm text-secondary leading-relaxed">
              Your custom booking request has been received. We'll review it and get back to you with a price offer. Check your email for confirmation.
            </p>
          </div>
          <div className="bg-white rounded-2xl border border-gray-100 px-6 py-4 w-full">
            <p className="font-manrope-medium text-xs text-secondary uppercase tracking-wide mb-1">Booking reference</p>
            <p className="font-manrope-bold text-lg text-primary">{submitted.bookingNumber}</p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 w-full">
            <button
              onClick={() => navigate("/account")}
              className="flex-1 py-3 bg-primary text-white font-manrope-semibold text-sm rounded-xl hover:opacity-90 transition-opacity"
            >
              View in account
            </button>
            <button
              onClick={() => navigate("/")}
              className="flex-1 py-3 border border-gray-200 text-secondary font-manrope-semibold text-sm rounded-xl hover:bg-gray-50 transition-colors"
            >
              Back to home
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-bg min-h-screen py-12 md:py-16">
      <div className="container mx-auto px-4 max-w-2xl">
        <div className="mb-8 text-center">
          <h1 className="font-manrope-bold text-3xl text-black mb-2">Custom booking request</h1>
          <p className="font-manrope-regular text-sm text-secondary">
            Describe the service you need, pick a date and time, and we'll get back to you with a price.
          </p>
        </div>

        <StepIndicator current={step} />

        <div className="bg-white rounded-2xl shadow-sm p-6 sm:p-8">
          {/* Step 0: Date & time slot */}
          {step === 0 && (
            <div className="flex flex-col gap-5">
              <h3 className="font-manrope-bold text-lg text-black">Choose date & time slot</h3>

              <div className="flex flex-col gap-1.5">
                <label className="font-manrope-medium text-sm text-secondary">Preferred date</label>
                <input
                  type="date"
                  min={getTodayStr()}
                  value={form.date}
                  onChange={set("date")}
                  className="border border-border rounded-xl px-4 py-2.5 font-manrope-regular text-sm text-black outline-none focus:border-primary transition-colors"
                />
              </div>

              <div className="flex flex-col gap-2">
                <label className="font-manrope-medium text-sm text-secondary">Preferred time slot</label>
                {loadingSlots ? (
                  <div className="flex flex-col gap-2">
                    {[1, 2, 3].map((i) => <div key={i} className="h-14 bg-gray-100 rounded-xl animate-pulse" />)}
                  </div>
                ) : slots.length === 0 ? (
                  <div className="bg-amber-50 border border-amber-100 rounded-xl px-4 py-4 text-center">
                    <p className="font-manrope-medium text-sm text-amber-600">No time slots available at the moment. Please check back later.</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {slots.map((slot) => (
                      <button
                        key={slot.id}
                        type="button"
                        onClick={() => { setForm((f) => ({ ...f, slotId: slot.id })); setError(""); }}
                        className={`flex items-center gap-3 px-4 py-3.5 rounded-xl border-2 text-left transition-all ${
                          form.slotId === slot.id
                            ? "border-primary bg-primary/5"
                            : "border-gray-100 hover:border-primary/30"
                        }`}
                      >
                        <div className={`w-4 h-4 rounded-full border-2 flex-shrink-0 flex items-center justify-center ${
                          form.slotId === slot.id ? "border-primary" : "border-gray-300"
                        }`}>
                          {form.slotId === slot.id && <div className="w-2 h-2 rounded-full bg-primary" />}
                        </div>
                        <div>
                          <p className={`font-manrope-semibold text-sm ${form.slotId === slot.id ? "text-primary" : "text-black"}`}>
                            {slot.startTime} – {slot.endTime}
                          </p>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Step 1: Service description */}
          {step === 1 && (
            <div className="flex flex-col gap-5">
              <h3 className="font-manrope-bold text-lg text-black">Describe your service request</h3>

              <div className="flex flex-col gap-1.5">
                <label className="font-manrope-medium text-sm text-secondary">
                  Service description <span className="text-primary">*</span>
                </label>
                <textarea
                  rows={6}
                  value={form.serviceDescription}
                  onChange={set("serviceDescription")}
                  placeholder="Tell us what service you need, any specific requirements, number of people or cars involved, etc."
                  className="border border-border rounded-xl px-4 py-3 font-manrope-regular text-sm text-black outline-none focus:border-primary transition-colors resize-none leading-relaxed"
                />
                <p className="font-manrope-regular text-xs text-secondary text-right">{form.serviceDescription.length} characters</p>
              </div>

              <div className="flex flex-col gap-1.5">
                <label className="font-manrope-medium text-sm text-secondary">Additional notes (optional)</label>
                <textarea
                  rows={3}
                  value={form.notes}
                  onChange={set("notes")}
                  placeholder="Any other information you'd like us to know…"
                  className="border border-border rounded-xl px-4 py-3 font-manrope-regular text-sm text-black outline-none focus:border-primary transition-colors resize-none"
                />
              </div>
            </div>
          )}

          {/* Step 2: Contact info */}
          {step === 2 && (
            <div className="flex flex-col gap-5">
              <h3 className="font-manrope-bold text-lg text-black">Your contact details</h3>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="flex flex-col gap-1.5">
                  <label className="font-manrope-medium text-sm text-secondary">Full name <span className="text-primary">*</span></label>
                  <input
                    type="text"
                    value={form.name}
                    onChange={set("name")}
                    placeholder="Your full name"
                    className="border border-border rounded-xl px-4 py-2.5 font-manrope-regular text-sm text-black outline-none focus:border-primary transition-colors"
                  />
                </div>
                <div className="flex flex-col gap-1.5">
                  <label className="font-manrope-medium text-sm text-secondary">Email <span className="text-primary">*</span></label>
                  <input
                    type="email"
                    value={form.email}
                    onChange={set("email")}
                    placeholder="your@email.com"
                    className="border border-border rounded-xl px-4 py-2.5 font-manrope-regular text-sm text-black outline-none focus:border-primary transition-colors"
                  />
                </div>
                <div className="flex flex-col gap-1.5">
                  <label className="font-manrope-medium text-sm text-secondary">Phone / WhatsApp</label>
                  <input
                    type="tel"
                    value={form.phone}
                    onChange={set("phone")}
                    placeholder="+1 000 000 0000"
                    className="border border-border rounded-xl px-4 py-2.5 font-manrope-regular text-sm text-black outline-none focus:border-primary transition-colors"
                  />
                </div>
                <div className="flex flex-col gap-1.5">
                  <label className="font-manrope-medium text-sm text-secondary">Address</label>
                  <input
                    type="text"
                    value={form.address}
                    onChange={set("address")}
                    placeholder="Pickup / service address"
                    className="border border-border rounded-xl px-4 py-2.5 font-manrope-regular text-sm text-black outline-none focus:border-primary transition-colors"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Step 3: Review */}
          {step === 3 && (
            <div className="flex flex-col gap-5">
              <h3 className="font-manrope-bold text-lg text-black">Review your request</h3>

              <div className="flex flex-col gap-3">
                {[
                  { label: "Date", value: form.date ? new Date(form.date + "T00:00:00").toLocaleDateString("en-US", { weekday: "long", year: "numeric", month: "long", day: "numeric" }) : "—" },
                  { label: "Time slot", value: selectedSlot ? `${selectedSlot.startTime} – ${selectedSlot.endTime}` : "—" },
                  { label: "Description", value: form.serviceDescription },
                  ...(form.notes ? [{ label: "Notes", value: form.notes }] : []),
                  { label: "Name", value: form.name },
                  { label: "Email", value: form.email },
                  ...(form.phone ? [{ label: "Phone", value: form.phone }] : []),
                  ...(form.address ? [{ label: "Address", value: form.address }] : []),
                ].map(({ label, value }) => (
                  <div key={label} className="bg-gray-50 rounded-xl px-4 py-3">
                    <p className="font-manrope-medium text-xs text-secondary uppercase tracking-wide mb-0.5">{label}</p>
                    <p className="font-manrope-regular text-sm text-black leading-relaxed">{value}</p>
                  </div>
                ))}
              </div>

              <div className="bg-primary/5 border border-primary/20 rounded-xl px-4 py-3">
                <p className="font-manrope-regular text-sm text-secondary leading-relaxed">
                  After submitting, our team will review your request and send you a price offer. You can then accept or negotiate via your account page.
                </p>
              </div>
            </div>
          )}

          {/* Error */}
          {error && (
            <p className="mt-4 font-manrope-regular text-sm text-red-500 bg-red-50 rounded-xl px-4 py-3">{error}</p>
          )}

          {/* Navigation */}
          <div className="flex gap-3 mt-6">
            {step > 0 && (
              <button
                type="button"
                onClick={back}
                className="flex-1 py-3 border border-gray-200 text-secondary font-manrope-semibold text-sm rounded-xl hover:bg-gray-50 transition-colors"
              >
                Back
              </button>
            )}
            {step < STEPS.length - 1 ? (
              <button
                type="button"
                onClick={next}
                disabled={step === 0 && slots.length === 0}
                className="flex-1 py-3 bg-primary text-white font-manrope-semibold text-sm rounded-xl hover:opacity-90 disabled:opacity-50 transition-opacity"
              >
                Continue
              </button>
            ) : (
              <button
                type="button"
                onClick={handleSubmit}
                disabled={isSubmitting}
                className="flex-1 py-3 bg-primary text-white font-manrope-semibold text-sm rounded-xl hover:opacity-90 disabled:opacity-60 transition-opacity flex items-center justify-center gap-2"
              >
                {isSubmitting && (
                  <svg className="animate-spin w-4 h-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                  </svg>
                )}
                {isSubmitting ? "Submitting…" : "Submit request"}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
